import { useEffect, useRef, useState, type FC, type LegacyRef } from 'react';
import {
  Button,
  DegreeCardSimple,
  Flex,
  Heading,
  Icon,
  Input,
  LoadingDots,
  Section,
  Select,
  SelectItem,
  Text,
} from '@highereducation/hen-ui';
import TcpaSection from '../TcpaSection';
import { SubmissionStatus } from '../ResultsPage/types';
import type { AdditionalQuestionsProps } from './types';
import { EventingLocation } from '../../types/enums';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import useViewEvents from '../../hooks/useViewEvents';
import useFlowTrackingEvents from '../../hooks/useFlowTrackingEvents';
import { useDebouncedFieldEvent } from '../PiiFormSection/hooks/useDebouncedFieldEvent';
import {
  createFormViewedPayload,
  createFieldEventsPayload,
  createElementViewedPayload,
} from '../../utils/createEventingPayloadsDE';
import {
  createPrqFormEventContext,
  createDslElementEventContext,
} from '../../utils/createPrqEventContext';

// Conversion-drawer element identity for the standalone PRQ form (spec §9.9.2).
// `ElementLoaded` fires when the form renders, `ElementViewed` when it becomes
// visible — both carry the hosted-lead-form `webElement` shape. `location`
// uses the shared `EventingLocation` enum (matching the exit-strategy CTA
// convention) rather than the spec doc's simplified literal.
const PRQ_ELEMENT_TYPE = 'dexp-hosted-lead-form';
const PRQ_ELEMENT_NAME = 'dexp-hosted-lead-form-additional';
const PRQ_ELEMENT_LOCATION = EventingLocation.CONVERSION_DRAWER;
const REQUIRED_FIELD_MESSAGE = 'This field is required';

const SCHOOL_LOGO_BASE_URL =
  'https://simple-storage-server.highereducation.com';

// The additional-questions step is a standalone PRQ form: TEXT questions render
// a text field, every other question type renders a dropdown. Field event
// `fieldType` mirrors that split so `FieldViewed` / `FieldInputted` stay aligned.
const getFieldType = (questionType?: string): string =>
  questionType === 'TEXT' ? 'text' : 'dropdown';

const getFieldName = (question: { id?: string }, index: number): string =>
  question.id || `question-${index}`;

const AdditionalQuestions: FC<AdditionalQuestionsProps> = ({
  tcpa,
  schoolName,
  schoolSlug,
  programName,
  questions,
  onProgramSubmit,
  tcpaSecondParagraph,
  programFlow,
  leadSubmissionStatus,
  onCloseExperience,
  productClickedCorrelationId,
  dslConfigId,
}) => {
  const isSubmitting = leadSubmissionStatus === SubmissionStatus.SUBMITTING;
  const isUnqualified = leadSubmissionStatus === SubmissionStatus.UNQUALIFIED;
  const schoolLogoUrl = schoolSlug
    ? `${SCHOOL_LOGO_BASE_URL}/${schoolSlug}.png?w=130&h=80`
    : '';

  const { eventContext } = useDynamicExperienceContext();
  const { triggerElementLoadedEvent } = useFlowTrackingEvents();
  const { sendDebouncedFieldEvent } = useDebouncedFieldEvent(
    productClickedCorrelationId,
    dslConfigId,
  );

  // PRQ form lifecycle events are emitted on the DSL path only (presence of
  // `dslConfigId`). The imperative result page renders this surface with no
  // `dslConfigId`, so it emits nothing and its payloads stay byte-identical
  // (AC #3 / RP-DSL-5.1 deferral boundary).
  const isDslPath = Boolean(dslConfigId);

  // Whether form-level events should fire: DSL path, qualified, with questions.
  // The disqualification (UNQUALIFIED) branch renders no form fields, so it
  // emits no element/form/field events.
  const shouldEmitFormEvents =
    isDslPath && !isUnqualified && Boolean(questions?.length);

  // Standalone PRQ form context (mirrors the PII confirmation form in
  // `PiiFormSection/Default.tsx`). The `FormViewed` and per-field `FieldViewed`
  // events ignore `dslConfigId` (their registered schemas do not define a
  // top-level `customDimensions` and the Tagular endpoint rejects it with HTTP
  // 400); the lead funnel correlates to the config via the product
  // `dslConfigId` dimension and the shared correlationId. Shared with the
  // debounced `FieldInputted` hook via `createPrqFormEventContext`.
  const prqEventContext = createPrqFormEventContext({
    eventContext,
    productClickedCorrelationId,
    dslConfigId,
  });

  // Conversion-drawer `webElement` identity shared by the PRQ `ElementLoaded`
  // (on render) and `ElementViewed` (on intersect). Built from the same
  // `createDslElementEventContext` helper as the exit-strategy CTA element
  // wiring: `dslConfigId` rides as a `customDimensions` entry on the DSL path,
  // carried on `ElementViewed` (the `ElementLoaded` schema drops it). The lead
  // funnel correlates to the published config via the product `dslConfigId`
  // dimension.
  const prqElementContext = createDslElementEventContext({
    eventContext,
    productClickedCorrelationId,
    elementType: PRQ_ELEMENT_TYPE,
    name: PRQ_ELEMENT_NAME,
    location: PRQ_ELEMENT_LOCATION,
    dslConfigId,
  });

  // `ElementLoaded` fires once when the PRQ form first renders (spec §9.9.1
  // row 6). Not viewport-gated — it mirrors the hosted-lead-form load event.
  const elementLoadedFiredRef = useRef(false);
  useEffect(() => {
    if (elementLoadedFiredRef.current || !shouldEmitFormEvents) return;
    elementLoadedFiredRef.current = true;
    triggerElementLoadedEvent(prqElementContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldEmitFormEvents]);

  // Fire `ElementViewed` + conversion-drawer `FormViewed` + one `FieldViewed`
  // per rendered question on viewport intersect.
  const { ref: formViewedRef } = useViewEvents(
    !shouldEmitFormEvents
      ? []
      : [
          createElementViewedPayload(prqElementContext),
          createFormViewedPayload(prqEventContext, true),
          ...questions!.map((question, index) => {
            const fieldName = getFieldName(question, index);
            return createFieldEventsPayload(
              {
                ...prqEventContext,
                fieldName,
                fieldType: getFieldType(question.questionType),
                fieldLabel: question.title ?? '',
                fieldId: fieldName,
              },
              'FieldViewed',
            );
          }),
        ],
  );

  // Debounced `FieldInputted`, gated to the DSL path so the imperative surface
  // emits nothing. Field name keys off `question.id` (same key the form data
  // and `FieldViewed` events use).
  const handleFieldInputted = (
    fieldName: string,
    fieldType: string,
    value: string,
  ) => {
    if (!isDslPath) return;
    sendDebouncedFieldEvent(fieldName, fieldType, value, true);
  };

  // Radix Select (which hen-ui Select wraps) doesn't contribute to FormData
  // natively. Local state + a hidden <input> per select field is the workaround
  // that lets `new FormData(e.currentTarget)` in handleSubmit pick up select
  // values. The test "syncs select value to hidden input and submits correctly"
  // protects this contract.
  const [selectValues, setSelectValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (fieldName: string, value: string) => {
    if (value.trim() === '') return;
    setFieldErrors((prev) => {
      if (!(fieldName in prev)) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleSelectChange = (fieldName: string, value: string) => {
    setSelectValues((prev) => ({ ...prev, [fieldName]: value }));
    handleFieldInputted(fieldName, 'dropdown', value);
    clearFieldError(fieldName, value);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data: Record<string, string | number> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    const errors: Record<string, string> = {};
    questions?.forEach((question, index) => {
      const fieldName = getFieldName(question, index);
      const value = (data[fieldName] ?? '').toString().trim();
      if (value === '') {
        errors[fieldName] = REQUIRED_FIELD_MESSAGE;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document.getElementById(firstErrorField)?.focus();
      }
      return;
    }

    setFieldErrors({});
    onProgramSubmit(data);
  };

  return (
    <Section
      asChild
      variant='light'
      padding='none'
      gap={null}
      minHeight='screen'
    >
      <form onSubmit={handleSubmit}>
        <Flex
          direction='column'
          gap='lg'
          className='px-section-sides py-section-top-bottom'
        >
          <Flex direction='row' align='center' gap='sm'>
            <Heading variant='h4'>
              This school requires some additional info.
            </Heading>
            <Icon icon='circle-alert' size='lg' color='secondary' aria-hidden />
          </Flex>

          <DegreeCardSimple
            layout='compact'
            schoolName={schoolName ?? ''}
            degreeTitle={programName ?? ''}
            schoolLogo={schoolLogoUrl}
          />

          {isUnqualified ? (
            <Flex direction='column' gap='sm'>
              <Text variant='b3' weight='bold'>
                Sorry, you do not meet the qualification requirements for this
                program. You can still explore other programs that may be a
                better fit for your background.
              </Text>
            </Flex>
          ) : (
            // `formViewedRef` wraps the visible question fields (not the
            // header), so the conversion-drawer `ElementViewed` / `FormViewed`
            // / per-question `FieldViewed` events fire when the actual form
            // content scrolls into view.
            <Flex
              direction='column'
              gap='md'
              ref={formViewedRef as LegacyRef<HTMLDivElement>}
            >
              {questions?.map((question, index) => {
                const fieldName = getFieldName(question, index);

                if (question.questionType === 'TEXT') {
                  return (
                    <Input
                      key={fieldName}
                      variant='two-line'
                      label={question.title ?? ''}
                      id={fieldName}
                      name={fieldName}
                      type='text'
                      error={Boolean(fieldErrors[fieldName])}
                      errorMessage={fieldErrors[fieldName]}
                      onChange={(e) => {
                        handleFieldInputted(fieldName, 'text', e.target.value);
                        clearFieldError(fieldName, e.target.value);
                      }}
                    />
                  );
                }

                return (
                  <div key={fieldName}>
                    <input
                      type='hidden'
                      name={fieldName}
                      value={selectValues[fieldName] ?? ''}
                    />
                    <Select
                      variant='two-line'
                      label={question.title ?? ''}
                      id={fieldName}
                      placeholder='Select...'
                      value={selectValues[fieldName]}
                      error={Boolean(fieldErrors[fieldName])}
                      errorMessage={fieldErrors[fieldName]}
                      onValueChange={(val) =>
                        handleSelectChange(fieldName, val)
                      }
                    >
                      {question.options?.map((option) => (
                        <SelectItem
                          key={option.optionId}
                          value={String(option.value ?? '')}
                        >
                          {option.text}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                );
              })}

              <TcpaSection
                title=''
                tcpaSecondParagraph={tcpaSecondParagraph}
                tcpa={tcpa}
                schoolId={schoolSlug}
                schoolName={schoolName}
                programFlow={programFlow}
              />
            </Flex>
          )}

          {isUnqualified && (
            <Flex direction='row'>
              <Button
                type='button'
                variant='primaryOutline'
                size='sm'
                onClick={onCloseExperience}
              >
                Close
              </Button>
            </Flex>
          )}
        </Flex>

        {!isUnqualified && (
          <>
            <Flex className='flex-1' />
            <Section
              variant='white-border-top'
              padding='section'
              position='sticky-bottom'
            >
              <Flex direction='column' gap='sm' align='center'>
                <Text variant='b4' weight='bold'>
                  Double check your information before submitting
                </Text>
                <Text variant='b5' color='subtext'>
                  Your information will never be shared with a school without
                  your consent.
                </Text>
                <Button
                  type='submit'
                  variant='primaryFill'
                  size='lg'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <LoadingDots
                      color='white'
                      label='Submitting additional information'
                    />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </Flex>
            </Section>
          </>
        )}
      </form>
    </Section>
  );
};

export default AdditionalQuestions;
